import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import zoneService from '../services/zoneService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface ZoneFormData {
  name: string;
  centerLatitude: string;
  centerLongitude: string;
  radiusKm: string;
  description: string;
  color: string;
  isVisible: boolean;
}

interface ZoneFormDialogProps {
  open: boolean;
  onClose: () => void;
  zoneId?: string;
  initialLatitude?: string;
  initialLongitude?: string;
  embedded?: boolean;
  onCreated?: () => void;
}

export const ZoneFormDialog: React.FC<ZoneFormDialogProps> = ({
  open,
  onClose,
  zoneId,
  initialLatitude,
  initialLongitude,
  embedded,
  onCreated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditMode = Boolean(zoneId);

  const [formData, setFormData] = useState<ZoneFormData>({
    name: '',
    centerLatitude: initialLatitude || '',
    centerLongitude: initialLongitude || '',
    radiusKm: '5',
    description: '',
    color: '#1d388d',
    isVisible: true,
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadZone = useCallback(async (zoneId: string) => {
    try {
      setLoading(true);
      const zone = await zoneService.getZoneById(zoneId);
      setFormData({
        name: zone.name,
        centerLatitude: zone.center_latitude.toString(),
        centerLongitude: zone.center_longitude.toString(),
        radiusKm: (zone.radius_meters / 1000).toString(),
        description: zone.description || '',
        color: zone.color,
        isVisible: zone.is_visible,
      });
    } catch (error) {
      console.error('Error loading zone:', error);
      toast.error(t('errors.generic'));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [t, onClose]);

  useEffect(() => {
    if (isEditMode && zoneId) {
      loadZone(zoneId);
    } else if (open) {
      // Reset form when dialog opens for new zone
      setFormData({
        name: '',
        centerLatitude: initialLatitude || '',
        centerLongitude: initialLongitude || '',
        radiusKm: '5',
        description: '',
        color: '#1d388d',
        isVisible: true,
      });
      setError('');
    }
  }, [zoneId, open, isEditMode, initialLatitude, initialLongitude, loadZone]);

  const handleChange = (field: keyof ZoneFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      isVisible: event.target.checked,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push(t('zone.validation.nameRequired'));
    }

    const lat = parseFloat(formData.centerLatitude);
    const lng = parseFloat(formData.centerLongitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      newErrors.push(t('validation.invalidCoordinates'));
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      newErrors.push(t('validation.invalidCoordinates'));
    }

    const radius = parseInt(formData.radiusKm);
    if (isNaN(radius) || radius < 1) {
      newErrors.push(t('zone.validation.radiusMin'));
    }
    if (radius > 1000) {
      newErrors.push(t('zone.validation.radiusMax'));
    }

    if (newErrors.length > 0) {
      setError(newErrors.join(', '));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const zoneData = {
        name: formData.name,
        center_latitude: parseFloat(formData.centerLatitude),
        center_longitude: parseFloat(formData.centerLongitude),
        radius_meters: parseInt(formData.radiusKm) * 1000, // Convert km to meters
        description: formData.description || undefined,
        color: formData.color,
        is_visible: formData.isVisible,
      };

      if (isEditMode && zoneId) {
        await zoneService.updateZone(zoneId, zoneData);
        toast.success(t('zone.updateSuccess'));
      } else {
        await zoneService.createZone(zoneData);
        toast.success(t('zone.createSuccess'));
        onCreated?.(); // Call onCreated callback if provided
      }

      onClose();
      // Navigate to zones list to refresh only if not embedded
      if (!embedded) {
        navigate('/zones');
      }
    } catch (error: unknown) {
      console.error('Error saving zone:', error);
      const err = error as { response?: { data?: { errors?: Record<string, string>; message?: string } } };
      if (err.response?.data?.errors) {
        setError(Object.values(err.response.data.errors).join(', '));
      } else {
        setError(err.response?.data?.message || t('errors.generic'));
      }
    } finally {
      setSaving(false);
    }
  };

  const formContent = (
    <Box component="form" onSubmit={handleSubmit}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('zone.fields.name')}
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('zone.fields.centerLatitude')}
                type="number"
                value={formData.centerLatitude}
                onChange={handleChange('centerLatitude')}
                required
                inputProps={{
                  step: 'any',
                  min: -90,
                  max: 90,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('zone.fields.centerLongitude')}
                type="number"
                value={formData.centerLongitude}
                onChange={handleChange('centerLongitude')}
                required
                inputProps={{
                  step: 'any',
                  min: -180,
                  max: 180,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('zone.fields.radiusKm')}
                type="number"
                value={formData.radiusKm}
                onChange={handleChange('radiusKm')}
                required
                inputProps={{
                  min: 1,
                  max: 1000,
                  step: 1,
                }}
                helperText={t('zone.fields.radiusHelp', 'Radius in kilometers (1-1000 km)')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('zone.fields.color')}
                type="color"
                value={formData.color}
                onChange={handleChange('color')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('zone.fields.description')}
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isVisible}
                    onChange={handleCheckboxChange}
                  />
                }
                label={t('zone.fields.isVisible')}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ flex: 1 }}
            >
              {saving ? <CircularProgress size={24} /> : (isEditMode ? t('common.update') : t('common.create'))}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={saving}
              sx={{ flex: 1 }}
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  if (!open) return null;

  return embedded ? (
    formContent
  ) : (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? t('zone.editTitle') : t('zone.createTitle')}
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};
